/**
 * Email Timeline Panel Component
 *
 * CENTER panel showing user details + email timeline.
 */

import React from "react";
import { EmailMessage, EmailConversation } from "./types";
import { Mail, MoreVertical } from "lucide-react";

interface EmailTimelinePanelProps {
  conversation: EmailConversation;
  onSelectEmail: (message: EmailMessage) => void;
  onReply: (message?: EmailMessage) => void;
  onOpenInsights: () => void;
  isMobile: boolean;
  selectedEmailId?: string;
  onBack: () => void;
  fullWidth?: boolean;
}

interface EmailItemProps {
  message: EmailMessage;
  isSelected: boolean;
  isFirst: boolean;
  isLast: boolean;
  onSelect: () => void;
}

const EmailItem: React.FC<EmailItemProps> = ({ message, isSelected, isFirst, isLast, onSelect }) => {
  const isUnread = !message.isRead;

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    const hour12 = hours % 12 || 12;
    return `${month} ${day},${year} • ${hour12}:${minutes}${ampm}`;
  };

  const bodyPreview =
    message.body?.replace(/<[^>]*>/g, "").trim().slice(0, 80) || "";

  const subjectDisplay = message.subject?.startsWith("Re:")
    ? message.subject
    : `Re: ${message.subject}`;

  return (
    <div
      onClick={onSelect}
      className="flex gap-0 py-3 cursor-pointer transition-colors relative"
    >
      {/* Timeline - Left side with mail icon and vertical line */}
      <div className="w-10 flex flex-col items-center flex-shrink-0">
        {/* Mail Icon */}
        <Mail size={16} className="text-[#7B7B7B] font-semibold dark:text-gray-500 mt-3" />
        {/* Vertical Line */}
        {!isLast && (
          <div className="w-[1px] bg-gray-300 dark:bg-gray-700 flex-1 mt-2" />
        )}
      </div>

      {/* Email Card */}
      <div className={`flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-lg border p-4 mr-4 transition-colors ${
        isSelected
          ? "border-[#7C83FD] dark:border-indigo-400 border-2 shadow-sm"
          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
      }`}>
        {/* Date and Chips Row */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] text-gray-500 dark:text-gray-400">
            {formatDateTime(message.sentAt)}
          </span>
          <div className="flex items-center gap-2">
            {isFirst && (
              <span className="px-2.5 py-0.5 bg-[#EEF0FF] dark:bg-indigo-500/15 text-[#7C83FD] dark:text-indigo-400 text-[11px] font-medium rounded">
                Recent
              </span>
            )}
            {isUnread && (
              <span className="px-2.5 py-0.5 bg-[#EEF0FF] dark:bg-indigo-500/15 text-[#7C83FD] dark:text-indigo-400 text-[11px] font-medium rounded">
                Unread
              </span>
            )}
          </div>
        </div>

        {/* Subject */}
        <h3 className="text-[14px] font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {subjectDisplay}
        </h3>

        {/* From / To */}
        <div className="text-[12px] text-gray-500 dark:text-gray-400 mb-2">
          <span className="text-[#7B7B7B] font-semibold dark:text-gray-500">From:</span> {message.from.name || message.from.email} &lt;{message.from.email}&gt;
          <span className="mx-3"></span>
          <span className="text-[#7B7B7B] font-semibold dark:text-gray-500">To:</span> {message.to[0]?.name || message.to[0]?.email} &lt;{message.to[0]?.email}&gt;
        </div>

        {/* Body Preview */}
        <p className={`text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed ${isSelected ? "underline decoration-[#7C83FD] decoration-2 underline-offset-2" : ""}`}>
          {bodyPreview}
        </p>
      </div>
    </div>
  );
};

export const EmailTimelinePanel: React.FC<EmailTimelinePanelProps> = ({
  conversation,
  onSelectEmail,
  selectedEmailId,
  fullWidth = false,
}) => {
  const recipient = conversation.participants.find(
    (p) => p.email !== "support@convoai.com"
  );

  const initials =
    recipient?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "??";

  const unreadCount = conversation.messages.filter((m) => !m.isRead).length;

  return (
    <div className={`${fullWidth ? "flex-1" : "w-[320px] min-w-[280px]"} bg-[#f5f6fa] dark:bg-gray-800/60 flex flex-col h-full overflow-hidden`}>
      {/* User Header */}
      <div className="px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Avatar */}
            <div className="w-[36px] h-[36px] rounded-full bg-[#7C83FD] flex items-center justify-center text-white text-[12px] font-medium flex-shrink-0">
              {initials}
            </div>

            {/* User Info */}
            <div className="min-w-0 flex-1">
              <h2 className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 truncate">
                {recipient?.name || "Unknown"}
              </h2>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                {recipient?.email}
              </p>
            </div>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-[#EEF0FF] dark:bg-indigo-500/15 text-[#7C83FD] dark:text-indigo-400 text-[10px] font-medium rounded whitespace-nowrap">
                {unreadCount} unread
              </span>
            )}
            <button className="p-1.5 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
              <MoreVertical size={16} />
            </button>
          </div>
        </div>

        {/* Tags */}
        <div className="flex items-center gap-2 mt-2">
          <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400 text-[10px] font-medium rounded">
            New Leads
          </span>
          <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px] font-medium rounded">
            Not Ordered
          </span>
        </div>
      </div>

      {/* Email List */}
      <div className="flex-1 overflow-y-auto py-2">
        {conversation.messages.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm">
            No emails found
          </div>
        ) : (
          conversation.messages.map((message, index) => (
            <EmailItem
              key={message.id}
              message={message}
              isSelected={message.id === selectedEmailId}
              isFirst={index === 0}
              isLast={index === conversation.messages.length - 1}
              onSelect={() => onSelectEmail(message)}
            />
          ))
        )}
      </div>
    </div>
  );
};
